import React, { FC, useEffect, useState } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { FlexAlign, FlexJustify } from '@src/types';
import { Flexbox } from '@src/views/containers';
import { useLocalTimeClock } from './useLocalTimeClock';
import { useTimerLogDialog } from './useTimerLogDialog';
import { formatClock, formatDuration, formatTimeOfDay } from './timeClockFormat';
import type { TimeClockContext, TimeClockSession } from './timeClockTypes';

const RESET_CONFIRM_TIMEOUT_MS = 4000;

const describeContext = ( context: TimeClockContext ): string =>
    [ context.clientName, context.offeringName ].filter( Boolean ).join( ' · ' );

const getLogStatusLabel = ( session: TimeClockSession ): string | null => {
    if( !session.clientOfferingId ) return null;
    if( session.logStatus === 'logged' ) return 'Logged';
    if( session.logStatus === 'dismissed' ) return 'Not logged';
    return 'Ready to log';
};

// Status display for the time clock. Timers are started from an offering's Hours panel,
// where the offering being worked on is known; the header only reports and stops them.
const HeaderTimeClock: FC = () => {
    const {
        active,
        isRunning,
        elapsedMs,
        todaySessions,
        todayTotalMs,
        stop,
        reset,
        removeSession,
        clearToday,
    } = useLocalTimeClock();
    const openTimerLogDialog = useTimerLogDialog();

    const [ isConfirmingReset, setIsConfirmingReset ] = useState( false ),
          [ isHistoryOpen, setIsHistoryOpen ]         = useState( false );

    useEffect( () => {
        if( !isConfirmingReset ) return;
        const timeoutId = window.setTimeout( () => setIsConfirmingReset( false ), RESET_CONFIRM_TIMEOUT_MS );
        return () => window.clearTimeout( timeoutId );
    }, [ isConfirmingReset ] );

    useEffect( () => {
        if( !isRunning ) setIsConfirmingReset( false );
    }, [ isRunning ] );

    const handleStop = () => {
        if( active?.clientOfferingId ){
            openTimerLogDialog();
            return;
        }
        stop();
    };

    const handleConfirmReset = () => {
        reset();
        setIsConfirmingReset( false );
    };

    const activeTarget = active ? ( active.offeringName || active.clientName ) : undefined;
    const statusLabel = isRunning
        ? `Tracking${ activeTarget ? ` · ${ activeTarget }` : '' }`
        : 'Tracked today';
    const statusTitle = active
        ? `Tracking ${ describeContext( active ) || 'time' }`
        : 'Start a timer from an offering\'s Hours panel';
    const readout = isRunning
        ? formatClock( elapsedMs )
        : todayTotalMs > 0 ? formatDuration( todayTotalMs ) : '0m';

    const historyContent = (
        <div className={ 'header-time-clock-history' }>
            <Flexbox
                align={ FlexAlign.CENTER }
                justify={ FlexJustify.SPACE_BETWEEN }
                className={ 'header-time-clock-history-header' }
            >
                <span className={ 'header-time-clock-history-title' }>Today</span>
                <span className={ 'header-time-clock-history-total' }>{ formatDuration( todayTotalMs ) }</span>
            </Flexbox>

            { todaySessions.length === 0 ? (
                <p className={ 'header-time-clock-history-empty' }>No sessions logged today.</p>
            ) : (
                <ul className={ 'header-time-clock-history-list' }>
                    { todaySessions.map( session => {
                        const contextLabel = describeContext( session );
                        const logStatusLabel = getLogStatusLabel( session );

                        return (
                            <li key={ session.id } className={ 'header-time-clock-history-row' }>
                                <span className={ 'header-time-clock-history-row-main' }>
                                    <span className={ 'header-time-clock-history-row-range' }>
                                        { formatTimeOfDay( session.startedAt ) } – { formatTimeOfDay( session.endedAt ) }
                                    </span>
                                    { contextLabel && (
                                        <span className={ 'header-time-clock-history-row-client' }>
                                            { contextLabel }
                                        </span>
                                    ) }
                                    { logStatusLabel && (
                                        <span className={ `header-time-clock-history-row-status is-${ session.logStatus ?? 'pending' }` }>
                                            { logStatusLabel }
                                        </span>
                                    ) }
                                </span>
                                <span className={ 'header-time-clock-history-row-duration' }>
                                    { formatDuration( session.durationMs ) }
                                </span>
                                <button
                                    type={ 'button' }
                                    className={ 'header-time-clock-history-row-remove' }
                                    title={ 'Remove session' }
                                    aria-label={ 'Remove session' }
                                    onClick={ () => removeSession( session.id ) }
                                >
                                    <Icon name={ 'close' } fitted />
                                </button>
                            </li>
                        );
                    } ) }
                </ul>
            ) }

            { todaySessions.length > 0 && (
                <button
                    type={ 'button' }
                    className={ 'header-time-clock-history-clear' }
                    onClick={ clearToday }
                >
                    Clear today
                </button>
            ) }
        </div>
    );

    return (
        <Flexbox
            align={ FlexAlign.CENTER }
            justify={ FlexJustify.START }
            noWrap
            className={ `header-time-clock${ isRunning ? ' is-running' : '' }` }
        >
            <span className={ 'header-time-clock-dot' } aria-hidden />

            <span className={ 'header-time-clock-readout' }>
                <span className={ 'header-time-clock-label' } title={ statusTitle }>{ statusLabel }</span>
                <span className={ 'header-time-clock-time' } aria-live={ 'off' }>{ readout }</span>
            </span>

            <span className={ 'header-time-clock-actions' }>
                { isRunning && !isConfirmingReset && (
                    <Button
                        type={ 'button' }
                        basic
                        compact
                        icon
                        size={ 'mini' }
                        className={ 'header-time-clock-btn' }
                        title={ 'Discard current session' }
                        aria-label={ 'Discard current session' }
                        onClick={ () => setIsConfirmingReset( true ) }
                    >
                        <Icon name={ 'undo' } />
                    </Button>
                ) }

                { isConfirmingReset && (
                    <span className={ 'header-time-clock-confirm' }>
                        <span className={ 'header-time-clock-confirm-label' }>Discard?</span>
                        <Button
                            type={ 'button' }
                            basic
                            compact
                            icon
                            size={ 'mini' }
                            className={ 'header-time-clock-btn header-time-clock-btn--danger' }
                            title={ 'Yes, discard' }
                            aria-label={ 'Yes, discard' }
                            onClick={ handleConfirmReset }
                        >
                            <Icon name={ 'check' } />
                        </Button>
                        <Button
                            type={ 'button' }
                            basic
                            compact
                            icon
                            size={ 'mini' }
                            className={ 'header-time-clock-btn' }
                            title={ 'Keep tracking' }
                            aria-label={ 'Keep tracking' }
                            onClick={ () => setIsConfirmingReset( false ) }
                        >
                            <Icon name={ 'close' } />
                        </Button>
                    </span>
                ) }

                { isRunning && !isConfirmingReset && (
                    <Button
                        type={ 'button' }
                        compact
                        icon
                        size={ 'mini' }
                        className={ 'header-time-clock-btn header-time-clock-btn--primary is-running' }
                        title={ active?.clientOfferingId ? 'Stop & log hours' : 'Stop timer' }
                        aria-label={ active?.clientOfferingId ? 'Stop and log hours' : 'Stop timer' }
                        onClick={ handleStop }
                    >
                        <Icon name={ 'stop' } />
                    </Button>
                ) }

                <Popup
                    on={ 'click' }
                    position={ 'bottom right' }
                    open={ isHistoryOpen }
                    onOpen={ () => setIsHistoryOpen( true ) }
                    onClose={ () => setIsHistoryOpen( false ) }
                    className={ 'header-time-clock-popup' }
                    content={ historyContent }
                    trigger={
                        <Button
                            type={ 'button' }
                            basic
                            compact
                            icon
                            size={ 'mini' }
                            className={ 'header-time-clock-btn' }
                            title={ "Today's sessions" }
                            aria-label={ "Today's sessions" }
                        >
                            <Icon name={ 'history' } />
                        </Button>
                    }
                />
            </span>
        </Flexbox>
    );
};

export default HeaderTimeClock;
